import { Suspense } from 'react';
import VerifiedReviewsWall from '@/components/VerifiedReviewsWall';
import Header from '@/components/Header';
import CartDrawer from '@/components/CartDrawer';
import WishlistDrawer from '@/components/WishlistDrawer';
import HeroVideo from '@/components/sections/HeroVideo';
import FeaturedProducts from '@/components/sections/FeaturedProducts';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

// Lazy load non-critical UI that isn't visible on initial paint
const BlogHighlights = lazyWithRetry(() => import('@/components/blog/BlogHighlights'));
const SocialSidebar = lazyWithRetry(() => import('@/components/SocialSidebar'));
const EmailSubscribePopup = lazyWithRetry(() => import('@/components/EmailSubscribePopup'));
const LoyaltyPopup = lazyWithRetry(() => import('@/components/LoyaltyPopup'));
const TrustBadges = lazyWithRetry(() => import('@/components/TrustBadges'));
const Footer = lazyWithRetry(() => import('@/components/sections/Footer'));
import { useAffiliateTracking } from '@/hooks/useAffiliateTracking';
import SEO from '@/components/SEO';

// Lazy load below-the-fold sections to reduce initial bundle and improve FCP
const HotspotHero = lazyWithRetry(() => import('@/components/sections/HotspotHero'));
const ColorPreviewer = lazyWithRetry(() => import('@/components/sections/ColorPreviewer'));
const GripComparison = lazyWithRetry(() => import('@/components/sections/GripComparison'));
const Collections = lazyWithRetry(() => import('@/components/sections/Collections'));
const GripXTechnology = lazyWithRetry(() => import('@/components/sections/GripXTechnology'));
const TeamSection = lazyWithRetry(() => import('@/components/sections/TeamSection'));
const GripCulture = lazyWithRetry(() => import('@/components/sections/GripCulture'));
const GripStatusMeter = lazyWithRetry(() => import('@/components/sections/GripStatusMeter'));
const Testimonials = lazyWithRetry(() => import('@/components/sections/Testimonials'));
const ExploreLinks = lazyWithRetry(() => import('@/components/sections/ExploreLinks'));

const Index = () => {
  // Track affiliate referrals from URL
  useAffiliateTracking();
  
  return (
    <div className="min-h-screen bg-background">
      <SEO url="/" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": "https://www.grizzleegripz.com/#organization",
            "name": "Grizzlee Gripz",
            "url": "https://www.grizzleegripz.com/",
            "logo": "https://www.grizzleegripz.com/lovable-uploads/5ef37510-2fdd-4779-962e-a1bea2b7b800.png",
            "sameAs": [
              "https://www.instagram.com/grizzleegripz",
              "https://www.tiktok.com/@grizzleegripz",
              "https://twitter.com/GrizzleeGripz"
            ]
          },
          {
            "@type": "WebSite",
            "@id": "https://www.grizzleegripz.com/#website",
            "url": "https://www.grizzleegripz.com/",
            "name": "Grizzlee Gripz",
            "publisher": { "@id": "https://www.grizzleegripz.com/#organization" },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.grizzleegripz.com/grips?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        ]
      })}} />
      <Header />
      <CartDrawer />
      <WishlistDrawer />
      <Suspense fallback={null}>
        <SocialSidebar />
        <LoyaltyPopup />
        <EmailSubscribePopup />
      </Suspense>
      
      <main>
        <HeroVideo />
        <FeaturedProducts />
        <Suspense fallback={null}>
          {/* ValentinesSection removed - Valentine's Day is over. Component preserved for next year. */}
          <GripComparison />
          <ColorPreviewer />
          <Collections />
          <TeamSection />
          <GripCulture />
          <HotspotHero />
          <ExploreLinks />
          <GripStatusMeter />
          <GripXTechnology />
          <Testimonials />
          <VerifiedReviewsWall type="overgrip" />
          <BlogHighlights />
        </Suspense>
      </main>
      
      <Suspense fallback={null}>
        <TrustBadges />
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
